import axios, { type AxiosInstance, type AxiosError } from 'axios'
import { useAuthStore } from '@/stores/authStore'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000,
})

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - handle 401
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Don't intercept 401s on the login endpoint itself — let the Login form
    // surface the error message (e.g. wrong password) inline.
    const requestUrl = error.config?.url || ''
    const isLoginRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/verify-totp')
    if (error.response?.status === 401 && !isLoginRequest) {
      const refreshToken = useAuthStore.getState().refreshToken
      if (refreshToken && error.config && !(error.config as Record<string, unknown>)._retry) {
        (error.config as Record<string, unknown>)._retry = true
        try {
          const res = await axios.post(`${API_URL}/v1/auth/refresh`, {
            refresh_token: refreshToken,
          })
          const { access_token, refresh_token } = res.data
          useAuthStore.getState().setTokens(access_token, refresh_token)
          error.config.headers.Authorization = `Bearer ${access_token}`
          return api(error.config)
        } catch {
          useAuthStore.getState().logout()
          window.location.href = '/login'
        }
      } else {
        useAuthStore.getState().logout()
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

export default api
